import React, { useEffect, useState } from 'react';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../state/store';
import useCsrService from '../../services/csr-api-redux';
import {
  GoAAccordion,
  GoABadge,
  GoABlock,
  GoAButton,
  GoAButtonGroup,
  GoACallout,
  GoADivider,
  GoAFormItem,
  GoAIcon,
  GoAModal,
  GoASpacer,
  GoATextArea,
} from '@abgov/react-components';
import {
  CollisiontTypeCopyGenerator,
  ContributingFactorsCopyGenerator,
  DamageCopyGenerator,
  DateAndTimeopyGenerator,
  DirectionOfTravelCopyGenerator,
  DriverAndVehicleCopyGenerator,
  LocationCopyGenerator,
  MediaCopyGenerater,
  PassengerInformationCopyGenerator,
  ReportStatusCopyGenerator,
} from './review-copy-generators';
import { fetchMediaListByCollisionId } from '../../state/slices/mediaSlice';
import styles from './review-component.module.css';
import moment from 'moment';
import { Collision } from '../../models/collision';
import _, { add } from 'lodash';
import {
  publishCollision,
  submitCollision,
  updateCollision,
  validateCollision,
} from '../../state/slices/collisionSlice';
import {
  getLoginInfoFromKeycloakJwt,
  isTheAccountVerifiedByGoA,
} from '../../state/slices/authSlice';
import {
  pushNotification,
  removeAllValidationErrorNotifications,
} from '../../state/slices/notificationSlice';
import { NotificationType } from '../notifications/notification-component';
import {
  getConfigurationByDescription,
  isHitAndRun,
  ReportStatus,
  resetReviewUIFlowState,
} from '../../state/slices/configurationSlice';
import { displaySection } from '../../common/ui-library';
import { useIsDesktop } from '../../utils/isDesktop';
import { useNavigate } from 'react-router-dom';
import { REACT_ALBERTA_CA_ACCOUNT_URL } from '../../state/configuration';
export interface ReviewComponentProps extends BaseWizardProps { }

export enum StepStatus {
  COMPLETED,
  INCOMPLETE,
  IN_PROGRESS,
  SKIPPED,
  CANNOT_START,
}

enum AccountType {
  BASIC,
  VERIFIED,
}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export const ReviewComponent = ({
  navigationConfiguration,
}: ReviewComponentProps) => {
  const isDesktop = useIsDesktop();

  const csrService = useCsrService();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const stateCollision = useAppSelector((state: RootState) => state.collision);
  const stateMedia = useAppSelector((state: RootState) => state.media);
  const stateAuth = useAppSelector((state: RootState) => state.auth);
  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );

  // Initialize with the computed value directly
  const loginInfo = getLoginInfoFromKeycloakJwt(stateAuth.auth.access.access);
  const accountVerifiedStatus = isTheAccountVerifiedByGoA(
    loginInfo.albertaVerified,
  );
  const [isAccountVerified, setIsAccountVerified] = useState(
    accountVerifiedStatus,
  );

  // UseEffect to monitor changes and ensure state stays in sync
  useEffect(() => {
    setIsAccountVerified(accountVerifiedStatus);
  }); // Re-run when stateAuth changes

  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [nextPage, setNextPage] = useState('');
  const [additionalDetails, setAdditionalDetails] = useState(
    stateCollision.collision.additionalDetails,
  );
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [isPrintView, setIsPrintView] = useState(false);
  const [isReadyToPrint, setIsReadyToPrint] = useState(false);
  const [isMapPrintReady, setIsMapPrintReady] = useState(false);
  const [arePhotosPrintReady, setArePhotosPringReady] = useState(false);

  const [showWarnings, setShowWarnings] = useState(false);
  const [showMediaSectionReminder, setShowMediaSectionReminder] =
    useState(false);

  useEffect(() => {
    const pageConfig = navigationConfiguration.pagingConfiguration.find(
      (item) => item.default !== undefined,
    );

    setNextPage(pageConfig?.default ?? 'error');
  }, []);

  useEffect(() => {
    dispatch(
      fetchMediaListByCollisionId({
        id: stateCollision.collision.id!,
        csrService,
      }),
    );
    if (
      stateConfiguration.uiTriggers.reviewDamageModified &&
      !stateConfiguration.uiTriggers.reviewMediaUpdated
    ) {
      dispatch(
        pushNotification({
          body: "You've updated your vehicle damage information. Update your photos to match the new information where applicable",
          notificationType: NotificationType.importantWarning,
        }),
      );
      setShowMediaSectionReminder(true);
    }

    if (
      stateConfiguration.uiTriggers.collisionTypeUpdated &&
      !stateConfiguration.uiTriggers.collisionSubTypeUpdated
    ) {
      dispatch(
        pushNotification({
          body: "One or more sections are incomplete after you made changes. Review and update the step(s) marked 'Incomplete'",
          notificationType: NotificationType.importantWarning,
        }),
      );
      setShowMediaSectionReminder(true);
    }

    dispatch(resetReviewUIFlowState());
  }, []);

  useEffect(() => {
    if (
      isPrintView &&
      isReadyToPrint &&
      isMapPrintReady &&
      arePhotosPrintReady
    ) {
      window?.print();
      window.close();
    }
  }, [isReadyToPrint, isMapPrintReady, arePhotosPrintReady]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsReadyToPrint(true);
    }); // Simulate a 2-second load time

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (imagesLoaded) handleSubmissionMessage();
  }, [imagesLoaded]);

  useEffect(() => {
    let pageConfig = 'error';

    pageConfig = navigationConfiguration.pagingConfiguration.find(
      (item) => item.default !== undefined,
    )!.default;
    setNextPage(pageConfig);
  }, []);

  useEffect(() => {
    const hash = window.location.hash.substring(1);

    // Split the hash into the path and query string
    const [path, queryString] = hash.split('?');

    // Parse the query string if it exists
    const params = queryString
      ? new URLSearchParams(queryString)
      : new URLSearchParams();

    if (params.has('print')) {
      setIsPrintView(true);
    }
  }, []);

  const getStepStatus = (step: number): StepStatus => {
    const stepStatus = stateCollision.collision.metadata!.formState.find(
      (item) => item.step === step,
    )!;
    if (stepStatus.completed) {
      return StepStatus.COMPLETED;
    } else if (stepStatus.inProgress) {
      return StepStatus.INCOMPLETE;
    } else if (stepStatus.skipped) {
      return StepStatus.SKIPPED;
    } else {
      return StepStatus.CANNOT_START;
    }
  };

  const getMediaStepStatus = (): StepStatus => {
    let stepStatus = StepStatus.CANNOT_START;

    if (
      getStepStatus(6) == StepStatus.SKIPPED ||
      getStepStatus(6) == StepStatus.INCOMPLETE
    ) {
      stepStatus = StepStatus.IN_PROGRESS;
    }

    if (getStepStatus(6) == StepStatus.COMPLETED) {
      stepStatus = StepStatus.COMPLETED;
    }

    return stepStatus;
  };

  const images = stateMedia.images.length > 0;

  const hasStepWarning = (stepNumber: number) => {
    if (!showWarnings) {
      return false; // Do not show warnings unless save() has been called
    }

    let stepStatus = getStepStatus(stepNumber);
    let retVal = false;

    if (stepStatus === StepStatus.INCOMPLETE) {
      retVal = true;
    } else {
      if (stepNumber === 6) {
        retVal = !images;
      }
    }

    return retVal;
  };

  const handleSubmissionMessage = () => {
    let submissionWarningMessage = '';
    const formState = stateCollision.collision.metadata!.formState;

    // Ignore status for contributing factors/passenger safety steps if this is a hit-and-run
    const excludedSteps =
      stateCollision.collision.collisionType &&
        isHitAndRun(stateCollision.collision.collisionType, stateConfiguration)
        ? [7, 9, 10]
        : [10];
    const incompleteSteps = formState.filter(
      (step) => !step.completed && !excludedSteps.includes(step.step),
    );

    const images = stateMedia.images.length > 0;
    const isImageRelated = !images;

    if (
      incompleteSteps.length > 1 ||
      (incompleteSteps.length == 1 && !isImageRelated)
    ) {
      submissionWarningMessage =
        'You cannot submit your report until all reporting steps are completed. Click on the link of incomplete section(s) to return and answer any outstanding questions';
    } else {
      if (!images)
        submissionWarningMessage =
          'Photos of damage areas must be uploaded to submit your collision report';
    }

    // Clear previous notifications and avoid adding duplicate messages
    dispatch(removeAllValidationErrorNotifications());

    if (submissionWarningMessage !== '') {
      // Dispatch warning notification
      dispatch(
        pushNotification({
          body: submissionWarningMessage,
          notificationType: NotificationType.importantValidationError,
        }),
      );
      return false;
    } else if (additionalDetails && additionalDetails?.length > 500) {
      return false;
    } else {
      return true;
    }
  };

  const getFullName = (): string => {
    if (
      stateCollision.collision.vehicles &&
      stateCollision.collision.vehicles![0].driver
    )
      return [
        stateCollision.collision.vehicles![0].driver!.firstName,
        stateCollision.collision.vehicles![0].driver!.middleName ?? '',
        stateCollision.collision.vehicles![0].driver!.lastName,
      ].join(' ');
    else return '';
  };

  const valid = async (): Promise<boolean> => {
    dispatch(removeAllValidationErrorNotifications());
    if (
      stateCollision.collision.collisionType &&
      isHitAndRun(stateCollision.collision.collisionType, stateConfiguration)
    ) {
      if (
        (
          stateCollision.collision.metadata?.formState.filter(
            (item) => !item.completed && ![10, 7, 9].includes(item.step),
          ) || []
        ).length > 0
      ) {
        return false;
      }
    } else if (
      (
        stateCollision.collision.metadata?.formState.filter(
          (item) => !item.completed && ![10].includes(item.step),
        ) || []
      ).length > 0
    ) {
      return false;
    }

    try {
      const validationResult = await dispatch(
        validateCollision({
          csrService,
          collision: stateCollision.collision,
        }),
      ).unwrap();
    } catch (error: any) {
      console.log(error);
      console.log('error in validation api');

      if (error && error.status === 400) {
        dispatch(
          pushNotification({
            body: error.data.detail || 'Validation failed.',
            notificationType: NotificationType.importantGenericError,
          }),
        );
        return false;
      }
    }

    return true;
  };

  const save = () => {
    // Set warnings to true to display invalid styles
    setShowWarnings(true);

    // Check for any warnings
    const hasWarnings = stateCollision.collision.metadata!.formState.some(
      (step) => hasStepWarning(step.step),
    );

    if (!hasWarnings && handleSubmissionMessage()) {
      let updatedCollision: Collision = _.cloneDeep(stateCollision.collision);

      updatedCollision.additionalDetails = additionalDetails;
      if (
        Array.isArray(updatedCollision?.vehicles) &&
        updatedCollision.vehicles[0]?.doesVehicleModelMatchWithMOVEs ===
        false &&
        updatedCollision.vehicles[0]?.modelNameFromMOVEs &&
        updatedCollision.vehicles[0].model?.toLocaleLowerCase() !=
        updatedCollision.vehicles[0]?.modelNameFromMOVEs.toLocaleLowerCase()
      ) {
        updatedCollision.vehicles[0].model =
          updatedCollision.vehicles[0].modelNameFromMOVEs;
      }

      dispatch(updateCollision(updatedCollision));
    }
  };

  const saveReport = (): Promise<void> => {
    const save = async () => {
      let updatedCollision: Collision = _.cloneDeep(stateCollision.collision);

      updatedCollision.additionalDetails = additionalDetails;

      if (
        Array.isArray(updatedCollision?.vehicles) &&
        updatedCollision.vehicles[0]?.doesVehicleModelMatchWithMOVEs ===
        false &&
        updatedCollision.vehicles[0]?.modelNameFromMOVEs &&
        updatedCollision.vehicles[0].model?.toLocaleLowerCase() !=
        updatedCollision.vehicles[0]?.modelNameFromMOVEs.toLocaleLowerCase()
      ) {
        updatedCollision.vehicles[0].model =
          updatedCollision.vehicles[0].modelNameFromMOVEs;
      }

      if (await valid()) {
        await dispatch(updateCollision(updatedCollision));
        await dispatch(publishCollision({ csrService }));
        if (navigationConfiguration.save) {
          navigationConfiguration.save();
        }
      }
    };
    return Promise.resolve(save());
  };

  const submitReport = async () => {
    let updatedCollision: Collision = _.cloneDeep(stateCollision.collision);

    updatedCollision.reportStatus = getConfigurationByDescription(
      stateConfiguration.configuration.collisionReportStatuses,
      ReportStatus.SUBMITTED,
    )!.id;

    if (await valid()) {
      dispatch(updateCollision(updatedCollision));
      dispatch(submitCollision({ csrService }));

      if (navigationConfiguration.save) {
        navigationConfiguration.save();
      }

      if (navigationConfiguration.navigate) {
        navigationConfiguration.navigate(nextPage);
      }
    }
  };

  const showPrint = () => {
    const newTabUrl = `/collision#review?print=true`;
    const printWindow = window.open(newTabUrl, '_blank');

    printWindow?.open();
  };

  return (
    <div>
      <GoAModal
        open={showSubmitModal}
        heading="Before submitting your collision report"
        children={
          <div>
            <h4 style={{ marginBottom: '-15px', marginTop: '-5px' }}>
              Ensure the following:
            </h4>
            <ul className="goa-unordered-list">
              <li>
                All photos of the vehicle damage clearly show the areas impacted
                by the collision.
              </li>
              <li>
                The location you've entered matches the location where the
                collision occurred.
              </li>
              <li>
                Your contact information is correct, as law enforcement may need
                to follow up with you.
              </li>
              <li>
                Print your unsubmitted report from the previous screen to keep a
                record if you wish
              </li>
            </ul>
          </div>
        }
        actions={
          <GoABlock>
            <GoAButton
              type="secondary"
              onClick={async () => {
                setShowSubmitModal(false);
              }}
            >
              Cancel
            </GoAButton>
            <GoAButton
              onClick={() => {
                setShowSubmitModal(false);
                submitReport();
              }}
            >
              Submit
            </GoAButton>
          </GoABlock>
        }
      ></GoAModal>
      <div>
        <GoABlock direction="row" alignment="start">
          <div style={{ flex: '1' }}>
            <h1>Review and additional collision details</h1>
          </div>

          {!isAccountVerified && (
            <div style={{ textAlign: 'left', maxWidth: '250px' }}>
              <GoAButton
                type="secondary"
                onClick={() =>
                  (window.location.href = 'https://www.alberta.ca')
                }
              >
                Register for a verified account
              </GoAButton>
              <p>You will need a verified account to complete this section.</p>
            </div>
          )}
        </GoABlock>
        <GoABlock direction="column">
          <HeaderSection
            fullName={getFullName()}
            accountType={
              isAccountVerified ? AccountType.VERIFIED : AccountType.BASIC
            }
            startDate={moment(stateCollision.collision.reportedDate).format(
              'MMMM DD, YYYY',
            )}
            reportId={stateCollision.collision.id!.toString()}
          />
          <ReportStatusCopyGenerator
            collision={stateCollision.collision}
            stateAuth={stateAuth}
            stateConfiguration={stateConfiguration}
          />
        </GoABlock>
        <GoASpacer vSpacing={'xs'} />

        <h4>
          The section links below allow you to navigate your reporting steps.
        </h4>
        <ul className="goa-unordered-list">
          <li>
            Click on the link for the section marked 'Incomplete' to resume your
            report
          </li>
          <li>
            Click on the link for the section marked 'Completed' to edit any of
            the information previously provided
          </li>
        </ul>
        <GoASpacer vSpacing={'2xl'} />
        <DetailsSection
          stepNumber="1"
          stepStatus={getStepStatus(1)}
          title="Collision Type"
          linkText="Collision type"
          linkPath="#collision-identification"
          showLink={getStepStatus(1) != StepStatus.CANNOT_START}
          hasStepWarning={hasStepWarning(1)}
        >
          <CollisiontTypeCopyGenerator
            collision={stateCollision.collision}
            stateConfiguration={stateConfiguration}
          />
          <div style={{ display: displaySection(!isDesktop) }}>
            <GoASpacer vSpacing={'l'} />
            <StatusSection stepStatus={getStepStatus(1)} />
          </div>
        </DetailsSection>
        <GoASpacer vSpacing={'2xl'} />
        <DetailsSection
          stepNumber="2"
          stepStatus={getStepStatus(2)}
          title="Location"
          linkText="Collision Location"
          linkPath="#location-check"
          showLink={getStepStatus(2) != StepStatus.CANNOT_START}
          hasStepWarning={hasStepWarning(2)}
        >
          <LocationCopyGenerator
            collision={stateCollision.collision}
            stateConfiguration={stateConfiguration}
            printView={isPrintView}
            showLocationText={true}
            onLoaded={() => setIsMapPrintReady(true)}
          />
          <div style={{ display: displaySection(!isDesktop) }}>
            <GoASpacer vSpacing={'l'} />
            <StatusSection stepStatus={getStepStatus(2)} />
          </div>
        </DetailsSection>
        <GoASpacer vSpacing={'2xl'} />
        <DetailsSection
          stepNumber="3"
          stepStatus={getStepStatus(3)}
          title="Date and time"
          linkText="Date and time"
          linkPath="#date-time"
          showLink={getStepStatus(3) != StepStatus.CANNOT_START}
          hasStepWarning={hasStepWarning(3)}
        >
          <DateAndTimeopyGenerator
            collision={stateCollision.collision}
            stateConfiguration={stateConfiguration}
          />
          <div style={{ display: displaySection(!isDesktop) }}>
            <GoASpacer vSpacing={'l'} />
            <StatusSection stepStatus={getStepStatus(3)} />
          </div>
        </DetailsSection>
        <GoASpacer vSpacing={'2xl'} />
        <DetailsSection
          stepNumber="4"
          stepStatus={getStepStatus(4)}
          title="Direction of travel and road details"
          linkText="Direction of travel and road details"
          linkPath="#direction-road-details-introduction"
          showLink={getStepStatus(4) != StepStatus.CANNOT_START}
          hasStepWarning={hasStepWarning(4)}
        >
          <DirectionOfTravelCopyGenerator
            collision={stateCollision.collision}
            stateConfiguration={stateConfiguration}
          />
          <div style={{ display: displaySection(!isDesktop) }}>
            <GoASpacer vSpacing={'l'} />
            <StatusSection stepStatus={getStepStatus(4)} />
          </div>
        </DetailsSection>
        <GoASpacer vSpacing={'2xl'} />
        <DetailsSection
          stepNumber="5"
          stepStatus={getStepStatus(5)}
          title="Vehicle damage"
          linkText="Vehicle damage"
          linkPath="#damage"
          showLink={getStepStatus(5) != StepStatus.CANNOT_START}
          hasStepWarning={hasStepWarning(5)}
        >
          <DamageCopyGenerator
            collision={stateCollision.collision}
            stateConfiguration={stateConfiguration}
          />
          <div style={{ display: displaySection(!isDesktop) }}>
            <GoASpacer vSpacing={'l'} />
            <StatusSection stepStatus={getStepStatus(5)} />
          </div>
        </DetailsSection>
        <GoASpacer vSpacing={'2xl'} />
        <DetailsSection
          stepNumber="6"
          stepStatus={getMediaStepStatus()} // media is a special case
          title="Photo upload"
          linkText="Photo uploads"
          linkPath="#media-introduction"
          sectionHelperText={
            showMediaSectionReminder ? (
              <GoACallout type="important" size="medium" mt={'l'}>
                Update your photos to match the new vehicle damage information
                where applicable
              </GoACallout>
            ) : (
              'You can skip this section but you will need to upload photos prior to submitting your report'
            )
          }
          showLink={getStepStatus(6) != StepStatus.CANNOT_START}
          hasStepWarning={hasStepWarning(6)}
          forceLinkBack={
            !(
              getStepStatus(7) == StepStatus.CANNOT_START ||
              getStepStatus(7) == StepStatus.INCOMPLETE ||
              getStepStatus(7) == StepStatus.IN_PROGRESS
            )
          }
        >
          <GoASpacer vSpacing={'l'} />
          {stateMedia && stateMedia.images && stateMedia.images.length > 0 ? (
            <div style={{ display: displaySection(!isPrintView) }}>
              <GoAAccordion heading={'Vehicle damage photos'}>
                <div className={styles.reviewaccordiantext}>
                  {stateMedia.images.length > 0
                    ? 'Click the thumbnail to view in full'
                    : 'You need to upload the vehicle damage photos before submitting your collision'}
                </div>
                <MediaCopyGenerater mediaState={stateMedia} />
              </GoAAccordion>
            </div>
          ) : (
            ''
          )}
          <div style={{ display: displaySection(!isDesktop) }}>
            <GoASpacer vSpacing={'l'} />
            <StatusSection
              stepStatus={
                getStepStatus(6) == StepStatus.INCOMPLETE ||
                  getStepStatus(6) == StepStatus.SKIPPED
                  ? StepStatus.IN_PROGRESS
                  : StepStatus.COMPLETED
              }
            />
          </div>
        </DetailsSection>
        <GoASpacer vSpacing={'2xl'} />
        <div
          style={{
            display: displaySection(
              stateCollision.collision.collisionType != null &&
              !isHitAndRun(
                stateCollision.collision.collisionType,
                stateConfiguration,
              ),
            ),
          }}
        >
          <DetailsSection
            stepNumber="7"
            stepStatus={getStepStatus(7)}
            title="Contributing factors"
            linkText="Contributing factors"
            linkPath="#contributing-factors-introduction"
            showLink={getStepStatus(7) != StepStatus.CANNOT_START}
            hasStepWarning={hasStepWarning(7)}
          >
            <div
              style={{
                display: displaySection(
                  stateCollision.collision.collisionType != null &&
                  !isHitAndRun(
                    stateCollision.collision.collisionType,
                    stateConfiguration,
                  ),
                ),
              }}
            >
              <ContributingFactorsCopyGenerator
                collision={stateCollision.collision}
                stateConfiguration={stateConfiguration}
              />
            </div>
            <div style={{ display: displaySection(!isDesktop) }}>
              <GoASpacer vSpacing={'l'} />
              <StatusSection stepStatus={getStepStatus(7)} />
            </div>
          </DetailsSection>
          <GoASpacer vSpacing={'2xl'} />
        </div>
      </div>
      <DetailsSection
        stepNumber={
          stateCollision.collision.collisionType &&
            isHitAndRun(
              stateCollision.collision.collisionType,
              stateConfiguration,
            )
            ? '7'
            : '8'
        }
        stepStatus={getStepStatus(8)}
        title="Driver and vehicle information"
        linkText="Driver and vehicle"
        linkPath="#driver-vehicle-introduction"
        showLink={
          isAccountVerified && getStepStatus(8) != StepStatus.CANNOT_START
        }
        showRegisterForVerifiedAccountCallout={!isAccountVerified}
        hasStepWarning={hasStepWarning(8)}
      >
        {isAccountVerified ? (
          <DriverAndVehicleCopyGenerator
            collision={stateCollision.collision}
            stateConfiguration={stateConfiguration}
          />
        ) : (
          ''
        )}
        <div style={{ display: displaySection(!isDesktop) }}>
          <GoASpacer vSpacing={'l'} />
          <StatusSection stepStatus={getStepStatus(8)} />
        </div>
      </DetailsSection>
      <GoASpacer vSpacing={'2xl'} />

      <div
        style={{
          display: displaySection(
            stateCollision.collision.collisionType != null &&
            !isHitAndRun(
              stateCollision.collision.collisionType,
              stateConfiguration,
            ),
          ),
        }}
      >
        <DetailsSection
          stepNumber="9"
          stepStatus={getStepStatus(9)}
          title="Passenger information"
          linkText="Passenger information"
          linkPath="#passenger-and-safety-info"
          showLink={getStepStatus(9) != StepStatus.CANNOT_START}
          hasStepWarning={hasStepWarning(9)}
        >
          <PassengerInformationCopyGenerator
            collision={stateCollision.collision}
            stateConfiguration={stateConfiguration}
          />
          <div style={{ display: displaySection(!isDesktop) }}>
            <GoASpacer vSpacing={'l'} />
            <StatusSection stepStatus={getStepStatus(9)} />
          </div>
        </DetailsSection>

        <GoASpacer vSpacing={'2xl'} />
      </div>

      <AdditionalDetails
        title="Review and additional collision details"
        setAdditionalDetails={setAdditionalDetails}
        additionalDetails={additionalDetails}
        collision={stateCollision.collision}
        isPrintView={isPrintView}
        hitAndRun={isHitAndRun(
          stateCollision.collision.collisionType!,
          stateConfiguration,
        )}
        stepNumber={
          stateCollision.collision.collisionType &&
            isHitAndRun(
              stateCollision.collision.collisionType,
              stateConfiguration,
            )
            ? '8'
            : '10'
        }
      />

      <div style={{ display: displaySection(!isPrintView) }}>
        <GoASpacer vSpacing={'2xl'} />
        <GoAButtonGroup alignment="start">
          <GoAButton
            onClick={() => {
              save();
              if (handleSubmissionMessage()) {
                setShowSubmitModal(true);
              }
            }}
          >
            Submit Report
          </GoAButton>
          <GoAButton
            type="secondary"
            onClick={() => {
              saveReport().then(() => {
                showPrint();
              });
            }}
          >
            Print Report
          </GoAButton>
          <GoAButton
            type="tertiary"
            onClick={() => {
              navigate('/dashboard');
            }}
          >
            Back to dashboard
          </GoAButton>
        </GoAButtonGroup>
      </div>

      <div style={{ display: displaySection(isPrintView) }}>
        <div id="print-view" />
        <GoASpacer vSpacing={'l'} />

        <h4>Vehicle damage photos</h4>
        <GoASpacer vSpacing={'l'} />
        {stateMedia.images.length > 0 ? (
          <MediaCopyGenerater
            key="1"
            mediaState={stateMedia}
            onPhotosReady={() => setArePhotosPringReady(true)}
          />
        ) : (
          ''
        )}
      </div>
    </div>
  );
};

export interface StatusSectionProps {
  stepStatus: StepStatus;
}

export const StatusSection = ({ stepStatus }: StatusSectionProps) => {
  return (
    <div style={{ marginRight: '10px' }}>
      {stepStatus == StepStatus.COMPLETED ? (
        <GoABadge type="success" content="Completed" />
      ) : null}
      {stepStatus == StepStatus.INCOMPLETE ? (
        <GoABadge type="important" content="Incomplete" />
      ) : null}
      {stepStatus == StepStatus.CANNOT_START ? (
        <GoABadge type="light" content="Cannot start yet" />
      ) : null}
      {stepStatus == StepStatus.IN_PROGRESS ? (
        <GoABadge type="information" content="In progress" />
      ) : null}
    </div>
  );
};

export interface DetailsSectionProps {
  children: React.ReactNode;
  stepNumber: string;
  stepStatus: StepStatus;
  title: string;
  showLink?: boolean;
  linkText: string;
  linkPath: string;
  sectionHelperText?: React.ReactNode;
  showRegisterForVerifiedAccountCallout?: boolean;
  hasStepWarning?: boolean;
  forceLinkBack?: boolean;
}

export const DetailsSection = ({
  stepNumber,
  stepStatus,
  title,
  children,
  showLink = true,
  linkText,
  linkPath,
  sectionHelperText,
  showRegisterForVerifiedAccountCallout,
  hasStepWarning,
  forceLinkBack = false,
}: DetailsSectionProps) => {
  const isDesktop = useIsDesktop();

  const getLink = (): string => {
    if (
      stepStatus === StepStatus.INCOMPLETE ||
      (stepStatus === StepStatus.IN_PROGRESS && !forceLinkBack)
    ) {
      return `${linkPath}`;
    } else {
      return `${linkPath}?returnTo=review`;
    }
  };

  return (
    <div>
      <h3 className={hasStepWarning ? styles.incompleteStep : ''}>
        {stepNumber}. {title}
      </h3>

      {showRegisterForVerifiedAccountCallout && (
        <div style={{ marginTop: '20px' }}>
          <RegisterForVerifiedAccountCallout />
        </div>
      )}

      {hasStepWarning && (
        <div className={styles.warningContainer}>
          <div className={styles.iconWrapper}>
            <GoAIcon type="warning" size="small" />{' '}
          </div>
          <p className={styles.warningText}>
            This section must be completed to submit your collision report
          </p>
        </div>
      )}
      {sectionHelperText ? (
        <div>
          <GoASpacer vSpacing={'2xs'} />
          <div className={styles.reviewcopy}>{sectionHelperText}</div>
        </div>
      ) : (
        ''
      )}
      <GoASpacer vSpacing={'l'} />
      <GoADivider />
      <GoASpacer vSpacing={'s'} />
      <div
        style={{
          display: 'flex',
          flexFlow: 'row',
          width: '100%',
        }}
      >
        <a
          style={{ marginLeft: '10px', display: displaySection(showLink) }}
          href={getLink()}
        >
          {linkText}
        </a>

        <div style={{ marginLeft: '10px', display: displaySection(!showLink) }}>
          {linkText}
        </div>
        <span style={{ flexGrow: '1' }}></span>
        <div style={{ display: displaySection(isDesktop) }}>
          <StatusSection stepStatus={stepStatus} />
        </div>
      </div>
      <div style={{ marginLeft: '10px' }}>{children}</div>
      <GoASpacer vSpacing={'s'} />
      <GoADivider />
    </div>
  );
};

export const RegisterForVerifiedAccountCallout = () => {
  return (
    <GoACallout heading="Register for a verified account" type="important">
      You will need a verified account to submit your report.
      <div>
        <a href={REACT_ALBERTA_CA_ACCOUNT_URL}>Register now</a>
      </div>
    </GoACallout>
  );
};

export interface AdditionalDetailsProps {
  collision: Collision;
  stepNumber: string;
  title: string;
  setAdditionalDetails: (additionalDetails: string) => void;
  additionalDetails?: string;
  isPrintView: boolean;
  hitAndRun: boolean;
}
export const AdditionalDetails = ({
  collision,
  stepNumber,
  title,
  setAdditionalDetails,
  additionalDetails,
  isPrintView = false,
  hitAndRun,
}: AdditionalDetailsProps) => {
  const getCallout = (): JSX.Element => {
    if (collision && collision.metadata) {
      if (
        (hitAndRun &&
          collision.metadata.formState.filter(
            (item) =>
              ![7, 9, 10].includes(item.step) && item.completed === false,
          ).length > 0) ||
        (!hitAndRun &&
          collision.metadata.formState.filter(
            (item) => item.step != 10 && item.completed === false,
          ).length > 0)
      ) {
        return (
          <div>
            <GoASpacer vSpacing={'m'} />
            <div style={{ marginLeft: '10px' }} className={styles.reviewcopy}>
              You will be able to submit your collision report once all report
              sections have been completed.
            </div>
          </div>
        );
      } else {
        return (
          <div>
            <GoASpacer vSpacing={'l'} />
            <GoACallout
              heading="All steps are completed, what to do next?"
              type="information"
              size="medium"
            >
              Review your answers carefully before submitting. To keep a record,
              click 'Print Report' below. Once submitted, you won't be able to
              access or edit your inputs. Click 'Submit Report' when you're
              ready.
            </GoACallout>
          </div>
        );
      }
    }

    return <div></div>;
  };

  return (
    <div>
      <h3>{title}</h3>
      {getCallout()}
      <GoASpacer vSpacing={'l'} />
      {isPrintView ? (
        <div className="review-copy">
          <GoASpacer vSpacing={'l'} />

          <strong>Additional collision details</strong>
          <div>{additionalDetails}</div>
          <GoASpacer vSpacing={'l'} />
        </div>
      ) : (
        <GoAFormItem
          requirement="optional"
          label="Additional collision details"
          helpText="Add any extra details about the collision for law enforcement to consider. Leave blank if not applicable."
        >
          <GoATextArea
            width="100%"
            name="additionalDetails"
            value={additionalDetails}
            maxCount={500}
            countBy="character"
            onChange={(item, value) => {
              setAdditionalDetails(value);
            }}
          ></GoATextArea>
        </GoAFormItem>
      )}
    </div>
  );
};

export interface HeaderSectionProps {
  fullName: string;
  accountType: AccountType;
  startDate: string;
  reportId: string;
}

export const HeaderSection = ({
  fullName,
  accountType,
  startDate,
  reportId,
}: HeaderSectionProps) => {
  return (
    <div>
      <h2>{fullName}</h2>
      <div style={{ color: '#666666' }}>
        Report Started <span className={'bluedot'}></span> {startDate}
      </div>
      <GoASpacer vSpacing={'l'} />
      <div>
        <GoABlock direction="row" gap={'m'}>
          <GoABadge type="information" content={'Report ID# ' + reportId} />
          {accountType == AccountType.BASIC ? (
            <GoABadge type="information" content={'Basic account'} />
          ) : null}
          {accountType == AccountType.VERIFIED ? (
            <GoABadge type="success" content={'Verified account'} />
          ) : null}
        </GoABlock>
      </div>
    </div>
  );
};

export interface ReviewSectionProps {
  title: string;
  link: string;
  reportStatus: ReportStatus;
  content: React.ReactNode;
}

export const ReviewSection = () => {
  return <div></div>;
};
function removeAlNotifications(): any {
  throw new Error('Function not implemented.');
}
